import type {
  Actor,
  Command,
  CommandResult,
  Comment,
  EntityRef,
  KpiSample,
  MyWorkRow,
  NeedsYouTally,
  ProjectEventEnvelope,
  ProjectState,
  Run,
} from "@minga/core"
import type {
  BriefHistory,
  KeyStatus,
  KpiHistory,
  PlatformRunnerState,
  RunnerListItemV1,
  RunsFilter,
  WorkspaceRole,
} from "@minga/protocol"
import { createContext, type ReactNode, useContext } from "react"
import type { AgentPageApi } from "@/features/agent-page/agent-api"
import type { ProjectTab } from "@/lib/project-views"
import type { KnowledgeApi } from "@/model/live/knowledge-api"
import type { LiveSnapshot } from "@/model/live/store"
import type { NeedsYouEntry, StuckItem } from "@/model/needs-you"
import type { ProjectModel, RunSummary, Teammate } from "@/model/view-types"

/**
 * Everything a project page's views need, built once per render of the project page (routes/project.tsx):
 * the live core state (optimistic view), the adapted view model (with ghosts), the Needs-you projection, and
 * `dispatch` for writes. Views read it with `useProject()`.
 */
/** A workspace's key status per provider (`HomeData.modelKeys` has the same shape); null = no key. */
export interface ModelKeyStatuses {
  readonly anthropic: KeyStatus | null
  readonly openai: KeyStatus | null
}

export interface ProjectContextValue {
  readonly workspaceId: string
  readonly projectId: string
  readonly viewer: Actor
  readonly userId: string
  /** The signed-in person's workspace role (H11), for `can()` (spec §9). */
  readonly role: WorkspaceRole
  /** The optimistic core state (confirmed + pending). */
  readonly state: ProjectState
  /** The view model of `state` with the open plan_change proposals folded in as ghosts. */
  readonly model: ProjectModel
  readonly ghostsByProposal: ReadonlyMap<string, ReadonlyArray<string>>
  readonly needs: ReadonlyArray<NeedsYouEntry>
  /** Items blocked with no card for anyone (sim fixes S04): shown under "Stuck" in Needs you. */
  readonly stuck: ReadonlyArray<StuckItem>
  /**
   * The one Needs-you count (decision 0056 §7.5): `needsTally.total`. The header's tab count, the phone tab bar, the
   * title, the Overview banner, Team and the chat aside all show this number.
   */
  readonly needsCount: number
  /** The Needs-you count by verb (core `tallyOf` over `needs`, `stuck`, `mine` and the items to sort). */
  readonly needsTally: NeedsYouTally
  /** My work (core `myWork`) for the signed-in person in this project (§5.4). */
  readonly mine: ReadonlyArray<MyWorkRow>
  /** The page's clock (ticks every 30 s), for derived values that depend on time. */
  readonly now: number
  readonly live: LiveSnapshot
  readonly runners: ReadonlyArray<RunnerListItemV1>
  /** False while the runner list (H8) is loading or failed to load: `runners` is then `[]`, not "none". */
  readonly runnersLoaded: boolean
  /** The workspace's model key status per provider (null: not loaded, or not readable). */
  readonly modelKeys?: ModelKeyStatuses | null
  /**
   * Decision 0166 §2: the workspace's Platform Runner and its subscriptions, so the lead's state says before a turn
   * waits whether this viewer's message could run. undefined while loading; null when unreadable.
   */
  readonly platform?: PlatformRunnerState | null | undefined
  /**
   * Decision 0166 §3.6: the viewer's choice of subscriptions in this project (`subscriptionKey`s); null or absent: all
   * they may use (nothing chosen, or not loaded).
   */
  readonly subscriptionChoice?: ReadonlyArray<string> | null | undefined
  readonly runnerNames: ReadonlyMap<string, string>
  readonly dispatch: (command: Command) => Promise<CommandResult>
  readonly dismissRejection: (commandId: string) => void
  /**
   * Open a run's transcript, or its agent chat. Native turns support an explicit details view too.
   */
  readonly openTranscript: (runId: string, options?: { readonly details: boolean }) => void
  /** Every run the page knows (the snapshot's plus any fetched by id), as summaries, newest first. */
  readonly runs: ReadonlyArray<RunSummary>
  /**
   * Temporary teammates (§3.7): live worker and validator runs, plus those that ended in the last 10 min, live first.
   */
  readonly teammates: ReadonlyArray<Teammate>
  /** The core run behind a summary (snapshot or fetched by id). */
  readonly runById: (runId: string) => Run | undefined
  /** Open the Runs sheet (`?runs=1`). */
  readonly openActivity: () => void
  /**
   * The tab last open in this project (item and goal pages keep it marked; the item page falls back to Plan, the
   * others to Overview). Undefined before any tab was open, and outside a
   * project route that tracks it (tests).
   */
  readonly lastTab?: ProjectTab | undefined
  /** "← Back" on item and goal pages: to the tab you came from, else the Overview (`backAction`). */
  readonly back?: () => void
  /**
   * Open a card in Needs you (decision 0056 §3.3): a proposal's card, a run's open permission prompt, or the tab.
   * `?view=needs&card=<id>` (`cardSearch`).
   */
  readonly openCard?: (target: {
    readonly runId?: string | undefined
    readonly proposalId?: string | undefined
  }) => void
  /** Open a proposal's card in Needs you (ref chips `P-n` in the chat and transcripts). */
  readonly openProposal?: (proposalId: string) => void
  /** Show an item on the plan's canvas (`?view=plan&layout=canvas&node=<id>`). */
  readonly showOnPlan?: (nodeId: string) => void
  /** Open an item's page (`/i/$ref`). */
  readonly openItem?: (itemId: string) => void
  /** Open a goal's page (`/g/$ref`). */
  readonly openGoal?: (goalId: string) => void
  /** `ProjectRpcs.History`: an entity's events, newest first. */
  readonly history?: (
    ref: EntityRef,
    beforeSeq: number | null,
    limit: number,
  ) => Promise<ReadonlyArray<ProjectEventEnvelope>>
  /** `ProjectRpcs.Thread`: comments on an entity. */
  readonly thread?: (target: EntityRef) => Promise<ReadonlyArray<Comment>>
  /** `ProjectRpcs.KpiSeries`: a KPI's samples (sparklines). */
  readonly kpiSeries?: (kpiId: string, from: number, to: number) => Promise<ReadonlyArray<KpiSample>>
  /** Decision 0097, `ProjectRpcs.KpiHistory`: the KPI page. */
  readonly kpiHistory?: (kpiId: string, from: number, to: number) => Promise<KpiHistory>
  /** Open a KPI's page (`/k/K-3`). */
  readonly openKpi?: (kpiId: string) => void
  /** `ProjectRpcs.Runs` (older runs for the Activity sheet); fetched runs join `runs`. */
  readonly fetchRuns?: (filter: RunsFilter, limit: number) => Promise<ReadonlyArray<Run>>
  /** `ProjectRpcs.Comments` (decisions 0049, 0051): the Overview's updates and decision comments. */
  readonly comments?: (
    kind: "update" | "decision",
    limit: number,
    perTarget?: boolean,
  ) => Promise<ReadonlyArray<Comment>>
  /** `ProjectRpcs.BriefHistory` (decision 0051): the brief's revisions, newest first. */
  readonly briefHistory?: (limit: number) => Promise<BriefHistory>
  /**
   * Wave K (decision 0065): search, past discussions and runs, decided cards and transcript deletion over the
   * project socket. Absent in tests that don't need them.
   */
  readonly knowledge?: KnowledgeApi
  /** Wave C (decision 0071): the agent page's reads over the project socket. Absent in tests that don't need them. */
  readonly agentPage?: AgentPageApi
  /** `LiveSnapshot.commentsSeq`: the comment reads refetch when it moves (absent: 0). */
  readonly commentsSeq?: number
  /** `LiveSnapshot.briefSeq`: the brief history refetches when it moves (absent: 0). */
  readonly briefSeq?: number
}

const ProjectContext = createContext<ProjectContextValue | null>(null)

export function ProjectProvider({ value, children }: { value: ProjectContextValue; children: ReactNode }) {
  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>
}

/** The project context when there is one (a run chip or dot also renders on workspace pages, without it). */
export function useOptionalProject(): ProjectContextValue | null {
  return useContext(ProjectContext)
}

export function useProject(): ProjectContextValue {
  const value = useContext(ProjectContext)
  if (!value) throw new Error("useProject outside a project page")
  return value
}
